/**
 * The seven scoring signals.
 *
 * Each exported function is a pure scorer returning a value in [0, 1].
 * They are orchestrated by the top-level `scoreAll` function, which
 * batches all embedding lookups so the full 7-signal pass costs exactly
 * one web-worker embedder invocation.
 *
 * The seven signals, in the order they appear in SubScores:
 *
 *   1. semantic   — full-document embedding cosine
 *   2. keyword    — TF-IDF overlap on tokenized words + bigrams
 *   3. bullet     — per-resume-bullet max-cosine against JD bullets
 *   4. section    — weighted per-section embedding cosine
 *   5. title      — title family classifier + pair score
 *   6. seniority  — years-of-experience band match
 *   7. field      — domain keyword classifier with cross-domain transfer
 */

import type {
  Bullet,
  JobDescription,
  Resume,
  SignalName,
  SubScores,
} from "./types.js";
import {
  classifyField,
  classifyTitleFamily,
  fieldTransfer,
  scoreTitleFamilies,
} from "./taxonomy.js";
import type { Embedder } from "./embeddings.js";
import {
  clamp01,
  cosineSimilarity,
  ngrams,
  tokenizeStemmed,
} from "./util.js";

// ---------------------------------------------------------------------------
// 1. semantic — whole-document embedding cosine
// ---------------------------------------------------------------------------
export async function scoreSemantic(
  resume: Resume,
  jd: JobDescription,
  embedder: Embedder,
): Promise<number> {
  const [rv, jv] = await embedder.embedBatch([resume.rawText, jd.rawText]);
  if (!rv || !jv) return 0;
  // Cosine on L2-normalized BGE is in roughly [0.40, 0.92] for real pairs.
  // Rescale so 0.40 → 0 and 0.85 → 1.0.
  const raw = cosineSimilarity(rv, jv);
  return clamp01((raw - 0.40) / 0.45);
}

// ---------------------------------------------------------------------------
// 2. keyword — stem-aware TF-IDF + bigram overlap (Day 8.5: added stemming)
// ---------------------------------------------------------------------------
function buildTfIdf(tokens: string[]): Map<string, number> {
  const tf = new Map<string, number>();
  for (const t of tokens) tf.set(t, (tf.get(t) ?? 0) + 1);
  // Normalize by total length so long docs don't dominate
  const total = tokens.length;
  if (total === 0) return tf;
  const out = new Map<string, number>();
  for (const [term, count] of tf) out.set(term, count / total);
  return out;
}

export function scoreKeyword(resume: Resume, jd: JobDescription): number {
  // Day 8.5: apply Porter stemming so "engineer"/"engineering"/"engineered"
  // all collapse to the same term. Big precision improvement on real
  // resume/JD pairs where the same skill shows up in different forms.
  const resumeUnigrams = tokenizeStemmed(resume.rawText);
  const jdUnigrams = tokenizeStemmed(jd.rawText);
  const resumeBigrams = ngrams(resumeUnigrams, 2);
  const jdBigrams = ngrams(jdUnigrams, 2);

  // Treat each token and each bigram as a term. Bigrams are rarer so they
  // carry higher weight when they match — this approximates an IDF boost.
  const resumeTerms = buildTfIdf([...resumeUnigrams, ...resumeBigrams]);
  const jdTerms = buildTfIdf([...jdUnigrams, ...jdBigrams]);

  // Score = fraction of JD terms that appear in the resume (hit rate).
  // Using distinct term count instead of TF-IDF mass avoids the dilution
  // problem where long documents with many unique bigrams suppress the score.
  if (jdTerms.size === 0) return 0;
  let hits = 0;
  let total = 0;
  for (const [term] of jdTerms) {
    total++;
    if (resumeTerms.has(term)) {
      // Bigram matches count double — multi-word phrases are more specific
      hits += term.includes(" ") ? 2 : 1;
    }
  }
  // Normalize: typical strong matches hit 30-50% of JD terms (many JD terms
  // are boilerplate that no resume would contain). Scale so 40% hit rate → 1.0.
  const raw = hits / (total * 0.4);
  return clamp01(raw);
}

// ---------------------------------------------------------------------------
// 3. bullet — greedy bipartite alignment between JD and resume bullets
//
// Day 8.5 upgrade: the original max-match let one strong resume bullet
// "cover" every JD requirement, inflating scores for candidates who happen
// to have one extremely generic strong bullet. Now we do greedy assignment:
// sort all (JD, resume) pairs by similarity descending, then assign each
// one subject to the constraint that each resume bullet can only match ONE
// JD bullet. This is a simple approximation of the optimal bipartite
// matching (Hungarian algorithm) that runs in O(R·J log R·J) and gives
// results within a few percent of optimal for the candidate pool sizes we
// handle (typically ≤40 resume bullets × ≤25 JD bullets).
// ---------------------------------------------------------------------------
export async function scoreBullets(
  resume: Resume,
  jd: JobDescription,
  embedder: Embedder,
): Promise<number> {
  const rBullets = resume.bullets.filter((b) => b.text.length > 10);
  const jBullets = jd.bullets.filter((b) => b.text.length > 10);

  if (rBullets.length === 0 || jBullets.length === 0) {
    // Fall back to section-text comparison if neither document was nicely
    // bulleted. Safe neutral score.
    return 0.5;
  }

  const allTexts = [
    ...rBullets.map((b) => b.text),
    ...jBullets.map((b) => b.text),
  ];
  const vecs = await embedder.embedBatch(allTexts);
  const rVecs = vecs.slice(0, rBullets.length);
  const jVecs = vecs.slice(rBullets.length);

  // Build the full similarity grid, then greedy-assign.
  interface Edge {
    jdIdx: number;
    resumeIdx: number;
    sim: number;
  }
  const edges: Edge[] = [];
  for (let jIdx = 0; jIdx < jVecs.length; jIdx++) {
    const jv = jVecs[jIdx];
    if (!jv) continue;
    for (let rIdx = 0; rIdx < rVecs.length; rIdx++) {
      const rv = rVecs[rIdx];
      if (!rv) continue;
      edges.push({
        jdIdx: jIdx,
        resumeIdx: rIdx,
        sim: cosineSimilarity(jv, rv),
      });
    }
  }
  edges.sort((a, b) => b.sim - a.sim);

  // Greedy assignment: walk highest-similarity edges first, assigning each
  // JD bullet to its best available resume bullet. Each resume bullet can
  // only be assigned to one JD bullet.
  const jdAssigned = new Array<number | null>(jVecs.length).fill(null);
  const resumeUsed = new Set<number>();
  for (const edge of edges) {
    if (jdAssigned[edge.jdIdx] !== null) continue;
    if (resumeUsed.has(edge.resumeIdx)) continue;
    jdAssigned[edge.jdIdx] = edge.sim;
    resumeUsed.add(edge.resumeIdx);
  }

  // Average over JD bullets. Any JD bullet that didn't get a match (because
  // it ran out of unused resume bullets) is scored 0 — an honest reflection
  // that the resume has nothing concrete to offer for that requirement.
  let sum = 0;
  let count = 0;
  for (const assigned of jdAssigned) {
    sum += assigned ?? 0;
    count += 1;
  }
  if (count === 0) return 0;
  const avg = sum / count;

  // Rescale: BGE cosine on bullet-length text tends to sit 0.20-0.80 on
  // real pairs. 0.20 → 0, 0.75 → 1.0.
  return clamp01((avg - 0.20) / 0.55);
}

// ---------------------------------------------------------------------------
// 4. section — weighted per-section cosine. Skills and Experience carry
//    more weight than Education or Interests in the final roll-up.
// ---------------------------------------------------------------------------
const SECTION_WEIGHT: Record<string, number> = {
  Skills: 1.0,
  Experience: 1.0,
  Summary: 0.5,
  Projects: 0.7,
  Education: 0.3,
  Certifications: 0.3,
  Publications: 0.3,
  Awards: 0.1,
  Languages: 0.1,
  Interests: 0.0,
  Misc: 0.2,
};

export async function scoreSections(
  resume: Resume,
  jd: JobDescription,
  embedder: Embedder,
): Promise<number> {
  const rSecs = Object.entries(resume.sections).filter(([, v]) => v.length > 40);
  // JD side: Responsibilities + Requirements drive the score. We compare
  // each meaningful resume section against the combined JD "what we want"
  // text rather than pairing section-to-section (too sparse at JD scale).
  const jdKey =
    [
      jd.sections.Responsibilities ?? "",
      jd.sections.Requirements ?? "",
      jd.sections.Preferred ?? "",
      jd.sections.About ?? "",
    ]
      .filter((s) => s.length > 0)
      .join("\n\n") || jd.rawText;

  if (rSecs.length === 0) return 0.5;

  const texts = [jdKey, ...rSecs.map(([, v]) => v)];
  const vecs = await embedder.embedBatch(texts);
  const jVec = vecs[0];
  if (!jVec) return 0;

  let weightedSum = 0;
  let weightTotal = 0;
  for (let i = 0; i < rSecs.length; i++) {
    const entry = rSecs[i];
    if (!entry) continue;
    const [name] = entry;
    const rVec = vecs[i + 1];
    if (!rVec) continue;
    const weight = SECTION_WEIGHT[name] ?? 0.3;
    if (weight === 0) continue;
    const sim = cosineSimilarity(jVec, rVec);
    // Rescale per section
    const scaled = clamp01((sim - 0.30) / 0.50);
    weightedSum += scaled * weight;
    weightTotal += weight;
  }

  if (weightTotal === 0) return 0.5;
  return clamp01(weightedSum / weightTotal);
}

// ---------------------------------------------------------------------------
// 5. title — family classification + pair score
// ---------------------------------------------------------------------------
export function scoreTitle(resume: Resume, jd: JobDescription): number {
  // Best-effort title extraction from the resume: first job title line
  // after "Experience", or failing that, look near top-of-doc.
  const resumeTitle = extractResumeTitle(resume);
  const rFamily = classifyTitleFamily(resumeTitle);
  const jFamily = classifyTitleFamily(jd.title ?? null);
  return scoreTitleFamilies(rFamily, jFamily);
}

function extractResumeTitle(resume: Resume): string | null {
  // Look inside the Summary/Experience sections for the first title-ish line
  const sources = [
    resume.sections.Experience,
    resume.sections.Summary,
    resume.rawText,
  ].filter((s): s is string => typeof s === "string" && s.length > 0);

  for (const src of sources) {
    const lines = src.split(/\r?\n/).slice(0, 20);
    for (const line of lines) {
      if (/engineer|scientist|manager|designer|developer|analyst|architect|lead|director|officer|researcher|physician|consultant|nurse|practitioner|attorney|lawyer|recruiter|marketer|coordinator|specialist|associate|partner|counsel|accountant/i.test(line)) {
        return line.trim().slice(0, 100);
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// 6. seniority — years-of-experience band match
// ---------------------------------------------------------------------------
const SENIORITY_BANDS = [
  { id: "entry", min: 0, max: 2 },
  { id: "mid", min: 2, max: 5 },
  { id: "senior", min: 5, max: 9 },
  { id: "staff", min: 9, max: 14 },
  { id: "principal", min: 14, max: 30 },
];

function bandForYears(years: number): string {
  for (const b of SENIORITY_BANDS) {
    if (years >= b.min && years < b.max) return b.id;
  }
  return "principal";
}

function extractYearsFromResume(resume: Resume): number {
  // Strategy: find all 4-digit years in the Experience section, take the
  // earliest and latest, return max(latest - earliest, count from "X+ years"
  // phrase in the summary).
  const expSection = resume.sections.Experience ?? resume.rawText;
  const years = [...expSection.matchAll(/\b(19|20)\d{2}\b/g)]
    .map((m) => Number.parseInt(m[0], 10))
    .filter((y) => y >= 1980 && y <= 2030);

  let spanYears = 0;
  if (years.length >= 2) {
    spanYears = Math.max(...years) - Math.min(...years);
  }

  const summary = resume.sections.Summary ?? resume.rawText.slice(0, 800);
  const phraseMatch = summary.match(/(\d+)\+?\s*(?:years?|yrs?)/i);
  const phraseYears = phraseMatch?.[1] ? Number.parseInt(phraseMatch[1], 10) : 0;

  return Math.max(spanYears, phraseYears);
}

function extractYearsFromJd(jd: JobDescription): number {
  const text = jd.rawText;
  const match = text.match(/(\d+)\+?\s*(?:years?|yrs?)\s*(?:of\s+)?(?:experience|exp)/i);
  return match?.[1] ? Number.parseInt(match[1], 10) : 0;
}

export function scoreSeniority(resume: Resume, jd: JobDescription): number {
  const rYears = extractYearsFromResume(resume);
  const jdYears = extractYearsFromJd(jd);

  if (jdYears === 0) return 0.8; // JD doesn't specify → slight positive prior

  const rBand = bandForYears(rYears);
  const jBand = bandForYears(jdYears);

  if (rBand === jBand) return 1.0;

  // Adjacent bands: 0.7. Two bands off: 0.4. Three+: 0.2.
  const ids = SENIORITY_BANDS.map((b) => b.id);
  const rIdx = ids.indexOf(rBand);
  const jIdx = ids.indexOf(jBand);
  const distance = Math.abs(rIdx - jIdx);
  if (distance === 1) return 0.7;
  if (distance === 2) return 0.4;
  return 0.2;
}

// ---------------------------------------------------------------------------
// 7. field — cross-domain transfer scoring
// ---------------------------------------------------------------------------
export function scoreField(resume: Resume, jd: JobDescription): number {
  const rField = classifyField(resume.rawText).field;
  const jField = classifyField(jd.rawText).field;
  return fieldTransfer(rField, jField);
}

// ---------------------------------------------------------------------------
// Top-level orchestrator — runs all seven scorers
// ---------------------------------------------------------------------------
export interface ScoreAllOptions {
  embedder: Embedder;
  signals?: ReadonlySet<SignalName>; // optional subset; defaults to all 7
}

export async function scoreAll(
  resume: Resume,
  jd: JobDescription,
  opts: ScoreAllOptions,
): Promise<SubScores> {
  // Default: score everything. Callers can subset for cheaper runs.
  const { embedder } = opts;

  const [semantic, bullet, section] = await Promise.all([
    scoreSemantic(resume, jd, embedder),
    scoreBullets(resume, jd, embedder),
    scoreSections(resume, jd, embedder),
  ]);

  return {
    semantic,
    keyword: scoreKeyword(resume, jd),
    bullet,
    section,
    title: scoreTitle(resume, jd),
    seniority: scoreSeniority(resume, jd),
    field: scoreField(resume, jd),
  };
}

/** Count the non-empty bullets across both docs (for diagnostics). */
export function totalBullets(resume: Resume, jd: JobDescription): number {
  const count = (bs: Bullet[]) => bs.filter((b) => b.text.length > 10).length;
  return count(resume.bullets) + count(jd.bullets);
}
